import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { GripVertical, Edit, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DraftTemplateField,
  getFieldTypeLabel,
  getFieldDescription,
} from "../../models/workPaperTemplate";
import { cn } from "../../lib/utils";

interface FieldListItemProps {
  field: DraftTemplateField;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  readOnly?: boolean;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export function FieldListItem({
  field,
  index,
  onEdit,
  onDelete,
  onMove,
  readOnly = false,
}: FieldListItemProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: "FIELD",
    item: { id: field.id, index, type: "FIELD" } satisfies DragItem,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !readOnly,
  });

  const [, drop] = useDrop({
    accept: "FIELD",
    hover(item: DragItem) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      onMove(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  const getFieldBadgeColor = (fieldType: string) => {
    switch (fieldType) {
      case "static":
        return "bg-gray-100 text-gray-800 border-gray-300";
      case "dynamic":
        return "bg-blue-100 text-blue-800 border-blue-300";
      case "attribute_summary_table":
        return "bg-green-100 text-green-800 border-green-300";
      case "sample_summary_table":
        return "bg-purple-100 text-purple-800 border-purple-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-3 p-3 border rounded-lg bg-white hover:bg-gray-50 transition-colors",
        isDragging && "opacity-50",
        field.isDeleted && "opacity-50 bg-red-50",
        field.isNew && "border-green-500",
        (field.isModified || field.isReordered) && "border-yellow-500",
      )}
    >
      {!readOnly && (
        <div className="cursor-grab active:cursor-grabbing text-gray-400">
          <GripVertical className="h-5 w-5" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Badge className={cn("text-xs", getFieldBadgeColor(field.fieldType))}>
            {getFieldTypeLabel(field.fieldType)}
          </Badge>
          {field.isNew && (
            <Badge
              variant="outline"
              className="text-xs bg-green-50 text-green-700 border-green-300"
            >
              New
            </Badge>
          )}
          {(field.isModified || field.isReordered) && !field.isNew && (
            <Badge
              variant="outline"
              className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300"
            >
              Modified
            </Badge>
          )}
          {field.isDeleted && (
            <Badge
              variant="outline"
              className="text-xs bg-red-50 text-red-700 border-red-300"
            >
              Deleted
            </Badge>
          )}
        </div>
        <p className="text-sm font-medium text-gray-900 truncate mb-0.5">
          {field.name}
        </p>
        <p className="text-xs text-gray-500 truncate">
          {getFieldDescription(field)}
        </p>
      </div>

      {!readOnly && !field.isDeleted && (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="h-8 w-8 p-0"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
